import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { salesApi, summaryApi, type CreateSaleInput, type DailySummary } from "./api";
import { usePaymentMethods } from "../payment-methods/hooks";

export const saleKeys = {
  all: ["sales"] as const,
  lists: () => [...saleKeys.all, "list"] as const,
  list: (params: Parameters<typeof salesApi.list>[0]) => [...saleKeys.lists(), params] as const,
  details: () => [...saleKeys.all, "detail"] as const,
  detail: (id: number) => [...saleKeys.details(), id] as const,
  // "range: all" (histórico) is a distinct cache bucket from any date range,
  // since it ignores from/to entirely.
  summary: (params: DailySummaryParams) =>
    params.range === "all"
      ? (["summary", "daily", "all"] as const)
      : (["summary", "daily", params.from ?? "today", params.to ?? params.from ?? "today"] as const),
};

export function useSales(params: Parameters<typeof salesApi.list>[0] = {}, enabled = true) {
  return useQuery({
    queryKey: saleKeys.list(params),
    queryFn: () => salesApi.list(params),
    enabled,
  });
}

export function useSale(id: number | null, enabled = true) {
  return useQuery({
    queryKey: saleKeys.detail(id ?? -1),
    queryFn: () => salesApi.get(id!),
    enabled: enabled && id != null,
  });
}

export function useCreateSale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSaleInput) => salesApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: saleKeys.lists() });
      qc.invalidateQueries({ queryKey: ["summary"] });
    },
  });
}

export function useSetNetReceived() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, netReceivedCents, paymentId }: { id: number; netReceivedCents: number; paymentId?: number }) =>
      salesApi.setNetReceived(id, netReceivedCents, paymentId),
    onSuccess: (sale) => {
      qc.setQueryData(saleKeys.detail(sale.id), sale);
      qc.invalidateQueries({ queryKey: saleKeys.lists() });
      qc.invalidateQueries({ queryKey: ["summary"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });
}

export function useVoidSale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      salesApi.voidSale(id, reason),
    onSuccess: (sale) => {
      qc.setQueryData(saleKeys.detail(sale.id), sale);
      qc.invalidateQueries({ queryKey: saleKeys.lists() });
      qc.invalidateQueries({ queryKey: ["summary"] });
    },
  });
}

export type DailySummaryParams = { from?: string; to?: string; range?: "all" };

export function useDailySummary(params: DailySummaryParams = {}) {
  return useQuery<DailySummary>({
    queryKey: saleKeys.summary(params),
    queryFn: () => summaryApi.daily(params),
    // Preset switches (Hoy/Semana/Mes/Histórico) change the queryKey
    // entirely; keep showing the previous range's numbers while the new one
    // loads instead of collapsing the whole view to a skeleton on every tap.
    placeholderData: keepPreviousData,
  });
}

// Convenience: combine the two sources needed by the sale panel.
export function usePaymentMethodsForSale() {
  const methods = usePaymentMethods();
  // sort by displayOrder
  const sorted = methods.data
    ? [...methods.data].sort((a, b) => a.displayOrder - b.displayOrder)
    : [];
  return { ...methods, data: sorted };
}
